<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core'
import { ref, computed } from 'vue'

import { COLORS_LIST } from '@/constants'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui'

const props = withDefaults(
  defineProps<{
    modelValue?: string
    id?: string
    disabled?: boolean
    defaultColor?: string
    colorList?: string[]
  }>(),
  {
    modelValue: '',
    id: '',
    disabled: false,
    defaultColor: '',
    colorList: undefined,
  }
)

const emit = defineEmits<{
  (event: 'update:modelValue', color: string | undefined): void
}>()

const chunkedColors = computed(() =>
  (props.colorList || COLORS_LIST)?.reduce((acc, color, index) => {
    const chunkIndex = Math.floor(index / 10)

    if (!acc[chunkIndex]) {
      acc[chunkIndex] = []
    }

    acc[chunkIndex].push(color)

    return acc
  }, [] as string[][])
)

const html5Color = ref<HTMLInputElement | null>(null)

const recentColorsStore = useLocalStorage<string[]>(`recentColors_${props.id}`, [])

function setRecentColor(color: string) {
  const index = recentColorsStore.value.indexOf(color)
  if (index !== -1) {
    recentColorsStore.value.splice(index, 1)
  }
  recentColorsStore.value.unshift(color)
  if (recentColorsStore.value.length > 10) {
    recentColorsStore.value.pop()
  }
}
const recentColors = computed(() => {
  return recentColorsStore.value.slice(0, 10)
})

function setColor(color: string | undefined) {
  if (color === undefined) {
    // clear color
    emit('update:modelValue', color)
    return
  }
  // check if color is correct
  const isCorrectColor = /^#([0-9A-F]{3}){1,2}$/i.test(color)
  if (isCorrectColor) {
    emit('update:modelValue', color)
    setRecentColor(color)
  }
}
const triggerHtml5Color = () => {
  html5Color.value?.click()
}
const handleColorChange = (e: Event) => {
  const input = e.target as HTMLInputElement
  setColor(input.value)
}
</script>

<template>
  <Popover>
    <PopoverTrigger :disabled="disabled">
      <slot />
    </PopoverTrigger>
    <PopoverContent hide-when-detached class="h-full w-full p-2" align="start" side="bottom">
      <div class="flex flex-col">
        <div class="rd-1 hover:bg-accent flex cursor-pointer items-center p-1" @click="setColor(undefined)">
          <span
            :class="[
              'inline-block h-6 w-6 cursor-pointer rounded-xs border p-0.5',
              defaultColor
                ? 'border-transparent'
                : 'hover:border-border relative after:absolute after:top-[10px] after:left-0 after:block after:h-0 after:w-6 after:rotate-[45deg] after:border-b-2 after:border-b-red-500 hover:shadow-xs',
            ]"
          >
            <span
              :class="{ defaultColor: 'relative block h-[18px] w-[18px] rounded-[2px] border-transparent' }"
              :style="`background-color: ${defaultColor || 'transparent'}`"
            >
              <svg
                viewBox="0 0 18 18"
                :style="`fill: ${defaultColor ? 'rgb(255, 255, 255)' : 'rgba(0, 0, 0, 0.4)'}; display: none`"
              >
                <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"></path>
              </svg>
            </span>
          </span>
          <span class="ml-1 text-sm">{{ defaultColor ? 'Default' : 'Remove Color' }}</span>
        </div>

        <span v-for="(items, index) in chunkedColors" :key="index" class="relative flex h-auto w-full p-0 last:pb-2">
          <span
            v-for="(item, i) in items"
            :key="i"
            class="flex-[0 0 auto] hover:border-border inline-block h-6 w-6 cursor-pointer rounded-xs border border-transparent p-0.5 hover:shadow-xs"
            @click="setColor(item)"
          >
            <span
              :style="{ backgroundColor: item }"
              class="relative block h-[18px] w-[18px] rounded-[2px] border-transparent"
            >
              <svg v-if="item !== modelValue" viewBox="0 0 18 18" style="fill: rgb(255, 255, 255); display: none">
                <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"></path>
              </svg>
              <svg
                v-else
                class="absolute top-[-1px] left-[1px] h-3 w-3"
                viewBox="0 0 18 18"
                style="fill: rgb(255, 255, 255); display: block"
              >
                <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"></path>
              </svg>
            </span>
          </span>
        </span>
        <div v-show="recentColors.length">
          <div class="my-1 text-sm">Recently Used</div>
          <span class="relative flex h-auto w-full p-0 last:pb-2">
            <span
              v-for="(item, index) in recentColors"
              :key="index"
              class="flex-[0 0 auto] hover:border-border inline-block h-6 w-6 cursor-pointer rounded-sm border border-transparent p-0.5 hover:shadow-xs"
              @click="setColor(item)"
            >
              <span
                class="relative block h-[18px] w-[18px] rounded-[2px] border-transparent"
                :style="{ backgroundColor: item }"
              >
                <svg viewBox="0 0 18 18" style="fill: rgb(255, 255, 255); display: none">
                  <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"></path>
                </svg>
              </span>
            </span>
          </span>
        </div>
        <div class="relative">
          <div class="hover:bg-accent px-1.5 py-1.5 text-sm hover:cursor-pointer" @click="triggerHtml5Color">
            Color Picker...
          </div>
          <input
            ref="html5Color"
            type="color"
            class="absolute top-4 left-0"
            style="visibility: hidden"
            @change="handleColorChange"
          />
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
